import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TranslateService } from './services/translate.service';
import { BackendHealthService } from './services/backend-health.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isBackendDown = false;

  constructor(
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly backendHealth: BackendHealthService
  ) {
    this.backendHealth.isBackendDown$.subscribe(
      (down) => (this.isBackendDown = down)
    );
  }

  private readonly ignoredUrls: string[] = [
    '/payment_transaction?file_id=',
    '/cback/v1.0/userdata/invalidateToken',
    '/cback/v1.0/access_token',
    '/partc/v1.0/user/',
    '/api/v1/favicon/',
    '/documents/translation/',
    '/api/v1/restart-process/',
    '/api/v1/delete-process/',
    '/version',
    '/user/account/change-password',
    '/user/account/password-policy'
  ];

  private isInfrastructureError(status: number): boolean {
    return status === 0 || status >= 500;
  }

  private shouldIgnoreUrl(url: string): boolean {
    return this.ignoredUrls.some((ignoredUrl) => url.includes(ignoredUrl));
  }

  /**
   * Recursively extracts all string values from a nested object/array.
   * Handles Django REST Framework error formats like:
   * - {"non_field_errors": ["error message"]}
   * - {"field_name": ["error1", "error2"]}
   */
  private extractNestedStrings(obj: unknown): string[] {
    if (typeof obj === 'string') return [obj];
    if (Array.isArray(obj))
      return obj.flatMap((item) => this.extractNestedStrings(item));
    if (obj && typeof obj === 'object') {
      return Object.values(obj).flatMap((val) =>
        this.extractNestedStrings(val)
      );
    }
    return [];
  }

  /**
   * Extracts a user-friendly error message from an HttpErrorResponse.
   * Handles multiple backend response formats:
   * - Parsed JSON: {message: "..."}, {detail: "..."}, {error: "..."}
   * - Raw string body (when Content-Type is not application/json)
   * - Null/empty body
   */
  extractErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;

    if (body && typeof body === 'object') {
      return this.extractFromObject(body);
    }

    if (typeof body === 'string' && body.length > 0) {
      return this.extractFromString(body);
    }

    return error.message || 'An error occurred';
  }

  private extractFromObject(body: any): string {
    if (typeof body.message === 'string') return body.message;
    if (typeof body.detail === 'string') return body.detail;
    if (Array.isArray(body.detail)) return body.detail.map(String).join(', ');
    if (typeof body.error === 'string') return body.error;
    const nested = this.extractNestedStrings(body);
    return nested.length > 0 ? nested.join(', ') : '';
  }

  private extractFromString(body: string): string {
    try {
      const parsed = JSON.parse(body);
      const result = this.extractFromObject(parsed);
      if (result) return result;
    } catch {
      // Not JSON, use the raw string
    }
    return body;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.shouldIgnoreUrl(request.url)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (this.isBackendDown && event instanceof HttpResponse) {
          this.backendHealth.reportHealthy();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle 403 Forbidden errors with context-aware redirect
        if (error.status === 403) {
          this.toastr.error($localize`:@@access_denied:Access denied`);
          // Redirect to Part B list if currently in Part B, otherwise to Part A dashboard
          const currentUrl = this.router.url;
          const redirectTo = currentUrl.startsWith('/part-b') ? '/part-b' : '/';
          this.router.navigate([redirectTo]);
          return throwError(() => error);
        }

        if (this.isInfrastructureError(error.status)) {
          this.backendHealth.reportUnhealthy();
          if (!this.isBackendDown) {
            const message =
              error.status === 0
                ? $localize`:@@unable_to_connect:Unable to connect to the server. Please check your connection.`
                : $localize`:@@service_temporarily_unavailable:Service temporarily unavailable. Please try again later.`;
            this.toastr.error(message);
          }
          return throwError(() => error);
        }

        const rawMessage = this.extractErrorMessage(error);
        try {
          this.toastr.error(this.translateService.localizeId(rawMessage));
        } catch {
          this.toastr.error(rawMessage);
        }

        return throwError(() => error);
      })
    );
  }
}
